import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Hint } from '@wit-support/ui';
import { cn } from './lib/cn.js';
// Полоса — тот же цвет, что у точки, но прозрачнее: светлые `*-bg` на фоне
// карточки почти не видны.
const BAND = {
    yes: 'bg-[var(--color-state-success-solid)]/30',
    unclear: 'bg-[var(--color-state-warning-solid)]/35',
    no: 'bg-[var(--color-state-danger-solid)]/30',
    none: 'bg-[var(--color-state-neutral-solid)]/30',
};
const DOT = {
    yes: 'bg-[var(--color-state-success-solid)]',
    unclear: 'bg-[var(--color-state-warning-solid)]',
    no: 'bg-[var(--color-state-danger-solid)]',
    none: 'bg-[var(--color-state-neutral-solid)]',
};
/** Доля шкалы `0…max` в процентах, прижатая к краям. */
export function paybackStripPos(x, max) {
    if (!(max > 0) || !Number.isFinite(x))
        return 0;
    // Две цифры после запятой: стиль `left` без хвостов вида 49.999999%.
    return Math.round(Math.min(100, Math.max(0, (x / max) * 100)) * 100) / 100;
}
const SIZE = {
    row: { box: 'h-3.5', band: 'top-1 h-1.5', dot: 'top-0.5 h-2.5 w-2.5', ring: 'top-0 h-3.5 w-3.5' },
    hero: { box: 'h-5', band: 'top-1.5 h-2', dot: 'top-0.5 h-4 w-4', ring: 'top-0 h-5 w-5' },
};
/**
 * Полоса «Вернётся на 1 $» (окупаемость): серая шкала `0…max`, пунктир на
 * пороге, цветная полоса «скорее всего от … до», точка — ожидание. Цвет — по
 * выводу (`tone`); без оценки — только шкала и порог. Числа рядом и подписи
 * шкалы рисует вызывающий (`PaybackStripScale`).
 */
export function PaybackStrip({ value, lo, hi, threshold, max, tone, marker, size = 'row', ariaLabel, hint, className, }) {
    const s = SIZE[size];
    const hasBand = lo !== null && hi !== null && Number.isFinite(lo) && Number.isFinite(hi);
    const left = hasBand ? paybackStripPos(Math.min(lo, hi), max) : 0;
    const right = hasBand ? paybackStripPos(Math.max(lo, hi), max) : 0;
    const strip = (_jsxs("div", { role: "img", "aria-label": ariaLabel, title: hint === undefined ? ariaLabel : undefined, tabIndex: hint === undefined ? undefined : 0, "data-tone": tone, className: cn('relative w-full min-w-[80px]', hint !== undefined &&
            'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]', s.box, className), children: [_jsx("div", { "aria-hidden": true, className: cn('absolute inset-x-0 rounded-full bg-[var(--color-state-neutral-solid)]/15', s.band) }), hasBand ? (_jsx("div", { "aria-hidden": true, "data-part": "band", className: cn('absolute rounded-full', s.band, BAND[tone]), style: {
                    left: `${left}%`,
                    width: `${Math.max(Math.round((right - left) * 100) / 100, 0.5)}%`,
                } })) : null, _jsx("div", { "aria-hidden": true, "data-part": "threshold", className: "absolute -bottom-px -top-px w-0 border-l-[1.5px] border-dashed border-[var(--color-text-muted)]", style: { left: `${paybackStripPos(threshold, max)}%` } }), marker !== null && marker !== undefined && Number.isFinite(marker) ? (_jsx("div", { "aria-hidden": true, "data-part": "marker", className: cn('absolute -translate-x-1/2 rounded-full border-2 border-[var(--color-text-muted)] bg-transparent', s.ring), style: { left: `${paybackStripPos(marker, max)}%` } })) : null, value !== null && Number.isFinite(value) ? (_jsx("div", { "aria-hidden": true, "data-part": "dot", className: cn('absolute -translate-x-1/2 rounded-full', s.dot, DOT[tone]), style: { left: `${paybackStripPos(value, max)}%` } })) : null] }));
    return hint === undefined ? strip : _jsx(Hint, { content: hint, children: strip });
}
/**
 * Приоритет при наложении: порог > значение > прошлое > lo/hi > края.
 * Подписи с меньшим приоритетом прячутся, если налезают на уже поставленные.
 */
export const PAYBACK_SCALE_LABEL_PRIORITY = [
    'threshold',
    'value',
    'marker',
    'lo',
    'hi',
    'zero',
    'max',
];
/**
 * Ширина символа подписи в % шкалы — грубая оценка для шкалы ~300–500 px
 * (12 px шрифт): точнее без измерения DOM не узнать, а мерить ради подписей
 * дорого. `GAP` — зазор между соседними подписями, тоже в %.
 */
const CHAR_PCT = 1.6;
/** Подпись прошлого периода мельче (11 px). */
const CHAR_PCT_SMALL = 1.3;
const GAP = 1;
const widthOf = (l) => l.label.length * (l.kind === 'marker' ? CHAR_PCT_SMALL : CHAR_PCT);
function extent(l) {
    const w = widthOf(l);
    if (l.anchor === 'start')
        return [l.pos, l.pos + w];
    if (l.anchor === 'end')
        return [l.pos - w, l.pos];
    return [l.pos - w / 2, l.pos + w / 2];
}
function collide(a, b) {
    const [a0, a1] = extent(a);
    const [b0, b1] = extent(b);
    return a0 < b1 + GAP && b0 < a1 + GAP;
}
/**
 * Подпись по центру точки у края шкалы вылезла бы за край (порог на 0 %,
 * значение за правым краем) — тогда она прижимается к краю: начинается от
 * точки вправо или кончается на точке. Точка остаётся под подписью.
 */
export function fitPaybackScaleLabel(l) {
    if (l.anchor !== 'center')
        return l;
    const half = widthOf(l) / 2;
    if (l.pos - half < 0)
        return { ...l, anchor: 'start' };
    if (l.pos + half > 100)
        return { ...l, anchor: 'end' };
    return l;
}
/**
 * Какие подписи шкалы показать: по приоритету, без наложений; у краёв —
 * прижатые к краю ({@link fitPaybackScaleLabel}). Чистая функция — для тестов
 * и для вызывающих, кому нужен тот же расклад.
 */
export function layoutPaybackScaleLabels(labels) {
    const rank = (k) => PAYBACK_SCALE_LABEL_PRIORITY.indexOf(k);
    const placed = [];
    for (const l of labels.map(fitPaybackScaleLabel).sort((a, b) => rank(a.kind) - rank(b.kind))) {
        if (placed.every((p) => !collide(p, l)))
            placed.push(l);
    }
    return placed;
}
const LABEL_CLASS = {
    threshold: 'text-[var(--color-text-primary)]',
    value: 'font-semibold text-[var(--color-text-primary)]',
    marker: 'text-[11px]',
    lo: '',
    hi: '',
    zero: '',
    max: '',
};
/**
 * Подписи под `PaybackStrip` размера `hero`: 0, границы полосы, порог, край
 * (`placement="below"`), либо над полосой — текущее значение и прошлое
 * (`placement="above"`). Наложения решаются приоритетом
 * {@link PAYBACK_SCALE_LABEL_PRIORITY}.
 */
export function PaybackStripScale({ threshold, max, thresholdLabel, maxLabel, lo, hi, value, marker, placement = 'below', className, }) {
    const at = (x) => paybackStripPos(x, max);
    const ok = (l) => !!l && Number.isFinite(l.at);
    const items = [];
    if (placement === 'above') {
        if (ok(value))
            items.push({ kind: 'value', label: value.label, pos: at(value.at), anchor: 'center' });
        if (ok(marker))
            items.push({ kind: 'marker', label: marker.label, pos: at(marker.at), anchor: 'center' });
    }
    else {
        items.push({ kind: 'threshold', label: thresholdLabel, pos: at(threshold), anchor: 'center' });
        if (ok(lo))
            items.push({ kind: 'lo', label: lo.label, pos: at(lo.at), anchor: 'center' });
        if (ok(hi))
            items.push({ kind: 'hi', label: hi.label, pos: at(hi.at), anchor: 'center' });
        items.push({ kind: 'zero', label: '0', pos: 0, anchor: 'start' });
        items.push({ kind: 'max', label: maxLabel, pos: 100, anchor: 'end' });
    }
    const shown = layoutPaybackScaleLabels(items);
    if (placement === 'above' && shown.length === 0)
        return null;
    return (_jsx("div", { "aria-hidden": true, "data-placement": placement, className: cn('relative h-4 text-xs text-[var(--color-text-muted)]', className), children: shown.map((l) => (_jsx("span", { "data-label": l.kind, className: cn('absolute whitespace-nowrap tabular-nums', l.anchor === 'center' && '-translate-x-1/2', LABEL_CLASS[l.kind]), style: l.anchor === 'end'
                ? { right: `${Math.round((100 - l.pos) * 100) / 100}%` }
                : { left: `${l.pos}%` }, children: l.label }, l.kind))) }));
}
/**
 * Мини-легенда под полосой: «● сейчас · ○ неделей раньше · ┆ порог 1,08 ·
 * полоса — скорее всего». Значки — того же цвета и формы, что на полосе.
 */
export function PaybackStripLegend({ tone, thresholdLabel, markerLabel, valueLabel = 'сейчас', bandLabel = 'скорее всего', className, }) {
    const item = 'inline-flex items-center gap-1 whitespace-nowrap';
    return (_jsxs("div", { "data-testid": "payback-strip-legend", className: cn('flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-[var(--color-text-muted)]', className), children: [_jsxs("span", { className: item, children: [_jsx("span", { "aria-hidden": true, className: cn('inline-block h-2 w-2 rounded-full', DOT[tone]) }), valueLabel] }), markerLabel ? (_jsxs("span", { className: item, children: [_jsx("span", { "aria-hidden": true, className: "inline-block h-2 w-2 rounded-full border-[1.5px] border-[var(--color-text-muted)]" }), markerLabel] })) : null, _jsxs("span", { className: item, children: [_jsx("span", { "aria-hidden": true, className: "inline-block h-2.5 w-0 border-l-[1.5px] border-dashed border-[var(--color-text-muted)]" }), thresholdLabel] }), _jsxs("span", { className: item, children: [_jsx("span", { "aria-hidden": true, className: cn('inline-block h-1.5 w-3 rounded-full', BAND[tone]) }), bandLabel] })] }));
}
//# sourceMappingURL=payback-strip.js.map